import {createAction} from '@packages/utilities/create-action';
import {
  FORM_SETTINGS_FETCH_REQUESTED,
  FORM_SETTINGS_FETCH_SUCCEEDED,
  FORM_SETTINGS_FETCH_FAILED,
  FORM_SETTINGS_UPDATE_REQUESTED,
  FORM_SETTINGS_UPDATE_SUCCEEDED,
  FORM_SETTINGS_UPDATE_FAILED,
  FORM_SETTINGS_UI_STATE_UPDATED,
} from '@packages/systems/designer/actions';
import type {FormSettingsPayload} from '@packages/systems/designer/forms/types';
import type {Map} from 'immutable';

/**
 * Action creators for Form Settings Store
 * Uses Webflow's createAction utility for consistent action creation and typing
 */

/**
 * Action to request fetching form settings
 * @param elementId - The element ID to fetch settings for
 * @param componentId - Optional component ID for component-specific settings
 */
export const fetchFormSettingsRequested = createAction<
  typeof FORM_SETTINGS_FETCH_REQUESTED,
  {
    elementId: string;
    componentId?: string;
  }
>(FORM_SETTINGS_FETCH_REQUESTED);

/**
 * Action dispatched when form settings fetch succeeds
 * @param elementId - The element ID that was fetched
 * @param componentId - Optional component ID for component-specific settings
 * @param response - The fetched form settings data
 */
export const fetchFormSettingsSucceeded = createAction<
  typeof FORM_SETTINGS_FETCH_SUCCEEDED,
  {
    response: FormSettingsPayload | null; // settings could be null if element id was just created
  }
>(FORM_SETTINGS_FETCH_SUCCEEDED);

/**
 * Action dispatched when form settings fetch fails
 * @param elementId - The element ID that failed to fetch
 * @param componentId - Optional component ID for component-specific settings
 * @param error - Error message or error object
 */
export const fetchFormSettingsFailed = createAction<
  typeof FORM_SETTINGS_FETCH_FAILED
>(FORM_SETTINGS_FETCH_FAILED);

/**
 * Action to request updating form settings
 * @param elementId - The form ID to update settings for
 * @param componentId - Optional component ID for component-specific settings
 * @param settings - The new form settings to apply
 */
export const updateFormSettingsRequested = createAction<
  typeof FORM_SETTINGS_UPDATE_REQUESTED,
  {
    elementId: string;
    componentId?: string;
    settings: FormSettingsPayload;
  }
>(FORM_SETTINGS_UPDATE_REQUESTED);

/**
 * Action dispatched when form settings update succeeds
 * @param elementId - The form ID that was updated
 * @param componentId - Optional component ID for component-specific settings
 * @param response - The updated form settings data from server
 */
export const updateFormSettingsSucceeded = createAction<
  typeof FORM_SETTINGS_UPDATE_SUCCEEDED,
  {
    response: FormSettingsPayload;
  }
>(FORM_SETTINGS_UPDATE_SUCCEEDED);

/**
 * Action dispatched when form settings update fails
 * @param elementId - The form ID that failed to update
 * @param componentId - Optional component ID for component-specific settings
 * @param error - Error message or error object
 */
export const updateFormSettingsFailed = createAction<
  typeof FORM_SETTINGS_UPDATE_FAILED
>(FORM_SETTINGS_UPDATE_FAILED);

/**
 * Action dispatched when form settings UI state is updated
 * @param elementId - The form ID that was updated
 * @param componentId - Optional component ID for component-specific settings
 * @param uiState - The new UI state to apply across open form settings
 */
export const formSettingsUiStateUpdated = createAction<
  typeof FORM_SETTINGS_UI_STATE_UPDATED,
  {
    elementId: string;
    componentId?: string;
    uiState: {
      isWebflowConfigured?: boolean;
      isEmailConfigured?: boolean;
    };
  }
>(FORM_SETTINGS_UI_STATE_UPDATED);

/**
 * Custom action types that include meta property for success/failure actions
 * RequestStore adds meta as Immutable Maps, which we convert to plain objects using .toJS()
 */

/** Success/failure actions with meta property */
interface ActionWithMeta<T extends string, P> {
  type: T;
  payload: P;
  meta: Map<string, string | undefined>;
}

/** Typed success actions */
type FetchFormSettingsSucceededAction = ActionWithMeta<
  typeof FORM_SETTINGS_FETCH_SUCCEEDED,
  {
    response: FormSettingsPayload | null; // settings could be null if element id was just created
  }
>;

type UpdateFormSettingsSucceededAction = ActionWithMeta<
  typeof FORM_SETTINGS_UPDATE_SUCCEEDED,
  {
    response: FormSettingsPayload;
  }
>;

/** Typed failure actions */
type FetchFormSettingsFailedAction = ActionWithMeta<
  typeof FORM_SETTINGS_FETCH_FAILED,
  null
>;

type UpdateFormSettingsFailedAction = ActionWithMeta<
  typeof FORM_SETTINGS_UPDATE_FAILED,
  null
>;

/**
 * Type for all form settings actions
 * Includes proper meta typing for success/failure actions
 */
export type FormSettingsAction =
  | ReturnType<typeof fetchFormSettingsRequested>
  | ReturnType<typeof updateFormSettingsRequested>
  | FetchFormSettingsSucceededAction
  | UpdateFormSettingsSucceededAction
  | FetchFormSettingsFailedAction
  | UpdateFormSettingsFailedAction
  | ReturnType<typeof formSettingsUiStateUpdated>;
